import { lazy, Suspense, useEffect, useState } from 'react'

import { useNetwork } from '@tribeplatform/react-sdk/hooks'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'

import { FallbackLoading } from '../common/components/FallbackLoading.js'
import { PlanBadge } from '../common/components/PlanBadge.js'
import { T } from '../i18n/components/T.js'

import type { AuditLogListProps } from './AuditLogList.js'

const AuditLogList = lazy(() =>
  import('./AuditLogList.js').then(m => ({ default: m.AuditLogList })),
)

export interface AuditLogsModalProps {
  open: boolean
  onClose: () => void
  filters?: AuditLogListProps['filters']
}

export const AuditLogsModal = ({
  open,
  onClose,
  filters = {},
}: AuditLogsModalProps) => {
  const [page, setPage] = useState<number>(1)
  const { data: network } = useNetwork({
    fields: {
      limits: 'all',
    },
  })

  // Reset the page to 1 if any of the filtering parameters change after the initial render
  useEffect(() => {
    /**
     * The effect skips the initial render and is only run if the filter parameters change
     * in subsequent updates.
     */
    return () => {
      setPage(1)
    }
  }, [filters])

  const authorized = !!network?.limits?.usageLimits.auditLogAccess

  return (
    <Modal open={open} onClose={onClose} size="2xl">
      <Modal.Header
        title={
          <div className="flex items-center space-s-2">
            <div>
              <T id="Generics.AuditLogs" defaultMessage="Audit logs" />
            </div>
            <PlanBadge authorized={authorized} />
          </div>
        }
      />
      <Modal.Content>
        <Suspense fallback={<FallbackLoading />}>
          <AuditLogList
            authorized={authorized}
            filters={filters}
            page={page}
            onPageChange={setPage}
          />
        </Suspense>
      </Modal.Content>
    </Modal>
  )
}
